<style scoped lang="scss">
  label {
    @apply text-gray-900 font-semibold text-12 mb-2;
  }

  textarea {
    @apply p-4 text-14 resize-none w-full border bg-none text-gray-900 border-gray-300 rounded-[8px] focus:border-secondary-300 hover:border-gray-500 transition-colors;
    box-shadow: none !important;

    &:focus,
    &:focus-visible,
    &:focus-within {
      box-shadow: none !important;
      outline: none !important;
    }

    &::placeholder {
      @apply text-gray-900 text-opacity-50;
    }
  }

  .disabled {
    textarea {
      @apply bg-gray-50;
    }
  }

  .hovering {
    textarea {
      @apply border-gray-500;
    }
  }

  .is-focussed {
    textarea {
      @apply border-secondary-300;
    }
  }
</style>

<template>
  <div :class="[className, { 'opacity-75 disabled cursor-not-allowed': disabled, 'is-focussed': isFocussed }]">
    <label v-if="label" :for="`id_${name}`" :class="[labelClass]" class="block mb-[4px]">
      {{ label }}
      <span
        v-if="(rules && typeof rules === 'string' && rules.includes('required') && !rules.includes('required_if')) || (typeof rules === 'object' && hasRequired) || fakeRequired"
        class="text-red"
      >
        *
      </span>
    </label>
    <div class="relative" :class="{ 'pointer-events-none': disabled, hovering: hovering }" @mouseover="hovering = true" @mouseleave="hovering = false">
      <textarea
        v-model="localValue"
        autocomplete="off"
        ref="textareaEl"
        @focus="handleFocus"
        @blur="handleBlur"
        @input="handleInput"
        :style="{ height: height }"
        :class="[inputClass, 'w-full appearance-none focus:outline-none', { '!border-red': shouldShowError }]"
        :id="`id_${name}`"
        :placeholder="placeholder"
        :disabled="disabled"
        :aria-describedby="`description_${name}`"
      />
    </div>
    <span v-if="shouldShowError" class="text-red text-12 block mt-2">{{ errorMessage }}</span>
  </div>
</template>

<script>
  import { useField } from "vee-validate";
  import { computed, ref, watch, onMounted } from "vue";

  export default {
    name: "FormTextarea",
    props: {
      name: {
        type: String,
        default: "",
      },
      value: {
        type: String,
        default: "",
      },
      placeholder: {
        type: String,
        default: "",
      },
      rules: {
        type: [String, Object],
        default: "",
      },
      label: {
        type: String,
        default: "",
      },
      showLabel: {
        type: Boolean,
        default: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      type: {
        type: String,
        default: "text",
      },
      autoFocus: {
        type: Boolean,
        default: false,
      },
      height: {
        type: String,
        default: "100px",
      },
      labelClass: {
        type: String,
        default: "text-12",
      },
      inputClass: {
        type: String,
        default: "",
      },
      className: {
        type: String,
        default: "",
      },
      fakeRequired: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["blur", "focus", "update:value", "input"],
    setup(props, { emit }) {
      const isFocussed = ref(false);
      const hovering = ref(false);
      const textareaEl = ref(null);

      // Use vee-validate's useField for validation
      const validateAs = computed(() => {
        if (props.label) {
          return props.label;
        }
        if (props.placeholder) {
          return props.placeholder;
        }
        if (props.name) {
          return props.name;
        }
        return `${props.type} field`;
      });

      const {
        value: localValue,
        errorMessage,
        meta,
        handleBlur: fieldBlur,
        setValue,
      } = useField(validateAs.value || props.name, props.rules, {
        initialValue: props.value,
        validateOnValueUpdate: true,
      });

      // Only show errors for touched fields
      const shouldShowError = computed(() => {
        return errorMessage.value && meta.touched;
      });

      const hasRequired = computed(() => {
        if (typeof props.rules === "object") {
          return !!props.rules.required;
        }
        return false;
      });

      const handleInput = (e) => {
        emit("input", e.target.value);
      };

      const handleBlur = (e) => {
        isFocussed.value = false;
        fieldBlur();
        emit("blur", e);
      };

      const handleFocus = (e) => {
        isFocussed.value = true;
        emit("focus", e);
      };

      // This watches for external changes to value
      watch(
        () => props.value,
        (newValue) => {
          if (String(newValue) !== String(localValue.value)) {
            setValue(newValue);
          }
        },
        { immediate: true },
      );

      // This emits changes from the input to the parent
      watch(localValue, (newValue) => {
        emit("update:value", newValue);
      });

      onMounted(() => {
        if (props.autoFocus && textareaEl.value) {
          textareaEl.value.focus();
        }
      });

      return {
        localValue,
        errorMessage,
        meta,
        isFocussed,
        hovering,
        textareaEl,
        validateAs,
        hasRequired,
        handleBlur,
        handleFocus,
        handleInput,
        shouldShowError,
      };
    },
  };
</script>
